import { Box } from '@zillow/constellation-10';
import Head from 'next/head';
import React from 'react';

import FooterTemplate from '../FooterTemplate/FooterTemplate';
import PostGrid from '../PostGrid/PostGrid';
import { buildPaginatedLink } from '../PostsPagination/buildPaginatedLink';

import { PostsTemplateProps } from './PostsTemplate.types';

const SidebarWrapper: React.FC<{
    children: React.ReactNode;
    sticky?: boolean;
    top?: string;
}> = ({ children, sticky = false, top = '24px' }) => (
    <Box
        css={
            sticky
                ? {
                      position: 'sticky',
                      alignSelf: 'flex-start',
                  }
                : undefined
        }
        style={sticky ? { top } : undefined}
    >
        {children}
    </Box>
);

export const PostsTemplate: React.FC<PostsTemplateProps> = ({
    archiveCategory,
    posts: { posts, pages },
    initialPage,
    baseLink,
    sidebar,
    footer,
    hero,
    query,
    incrementPaginationLinks,
    stickySidebarTop,
}) => {
    return (
        <>
            <Head>
                {initialPage < pages && (
                    <link
                        rel="next"
                        href={buildPaginatedLink({
                            baseLink,
                            query,
                            page: initialPage + 1,
                        })}
                    />
                )}
                {initialPage > 1 && (
                    <link
                        rel="prev"
                        href={buildPaginatedLink({
                            baseLink,
                            query,
                            page: initialPage - 1,
                        })}
                    />
                )}
            </Head>
            <Box
                css={{
                    marginBlock: '0',
                    marginInline: 'auto',
                    maxWidth: 'breakpoint-xxl',
                    paddingTop: 'loose',
                    paddingBottom: 'layout.looser',
                    paddingInline: 'default',
                    lg: {
                        paddingTop: 'looser',
                    },
                }}
            >
                {hero && (
                    <Box
                        css={{
                            marginBottom: 'tighter',
                            md: {
                                marginBottom: 'loose',
                            },
                            xl: {
                                marginBottom: 'layout.looser',
                            },
                        }}
                    >
                        {hero}
                    </Box>
                )}

                <Box
                    css={{
                        lg: {
                            display: 'flex',
                        },
                    }}
                >
                    <Box
                        css={{
                            flex: '1 1 0',
                            minWidth: '0',
                            marginBottom: 'looser',
                        }}
                    >
                        {posts && (
                            <PostGrid
                                incrementPaginationLinks={
                                    incrementPaginationLinks
                                }
                                posts={posts}
                                pages={pages}
                                page={initialPage}
                                baseLink={baseLink}
                                query={query}
                                archiveCategory={archiveCategory}
                            />
                        )}
                    </Box>
                    {sidebar && (
                        <Box
                            css={{
                                marginTop: 'layout.looser',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                width: '300px',
                                lgDown: {
                                    width: '100%',
                                    marginTop: '0',
                                },
                                lg: {
                                    marginLeft: 'loose',
                                    marginRight: '0',
                                    marginTop: '0',
                                },
                            }}
                        >
                            <SidebarWrapper
                                sticky={true}
                                top={stickySidebarTop}
                            >
                                {sidebar}
                            </SidebarWrapper>
                        </Box>
                    )}
                </Box>
            </Box>
            {footer && <FooterTemplate footer={footer} />}
        </>
    );
};

export default PostsTemplate;
